import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar } from "@/components/ui/calendar";
import { Lock, Loader2, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format, addDays } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1685459347149-ab8bb8c48a21?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200";

const GLM_LOGO =
  "https://customer-assets.emergentagent.com/job_payment-hub-260/artifacts/ain0zi0z_WhatsApp%20Image%202026-03-10%20at%208.23.52%20AM%20%281%29.jpeg";

const PROPERTY_IMAGES = {
  listing_1:
    "https://images.unsplash.com/photo-1698335444303-963ad8397e12?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=300",
  listing_2:
    "https://images.unsplash.com/photo-1645237455598-e8f02d706a4e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=300",
  listing_3:
    "https://images.unsplash.com/photo-1652882860902-7c6b0f88ef23?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=300",
};

const CURRENCY_SYMBOLS = { eur: "€", usd: "$", gbp: "£" };

// Allowed check-in/out time slots
const CHECK_IN_TIMES = [];
for (let h = 14; h <= 22; h++) {
  CHECK_IN_TIMES.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 22) CHECK_IN_TIMES.push(`${String(h).padStart(2, "0")}:30`);
}
const CHECK_OUT_TIMES = [];
for (let h = 0; h <= 11; h++) {
  CHECK_OUT_TIMES.push(`${String(h).padStart(2, "0")}:00`);
  if (h < 11) CHECK_OUT_TIMES.push(`${String(h).padStart(2, "0")}:30`);
}

const parseLocalDate = (dateStr) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const BLANK_GUEST = {
  guest_name: "",
  guest_email: "",
  guest_phone: "",
  guest_count: 1,
  check_in_time: "",
  check_out_time: "",
  parking_required: false,
  special_requirements: "",
};

const HomePage = () => {
  const [listings, setListings] = useState([]);
  const [selectedListing, setSelectedListing] = useState(null);
  const [selectedDates, setSelectedDates] = useState(new Set());
  const [anchorDate, setAnchorDate] = useState(null);
  const [guest, setGuest] = useState({ ...BLANK_GUEST });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchListings = async () => {
      try {
        const res = await axios.get(`${API}/listings`);
        setListings(res.data);
      } catch {
        toast.error("Failed to load listings. Please refresh.");
      } finally {
        setFetching(false);
      }
    };
    fetchListings();
  }, []);

  const nights = selectedDates.size;
  const sortedSelectedDates = Array.from(selectedDates).sort();

  const calcPrice = () => {
    if (!selectedListing || selectedDates.size === 0)
      return { net: 0, weekdayNights: 0, weekendNights: 0, weekdayNet: 0, weekendNet: 0 };
    const wdRate = selectedListing.rate_per_night;
    const weRate = selectedListing.weekend_rate || null;
    let weekdayNights = 0, weekendNights = 0, weekdayNet = 0, weekendNet = 0;
    for (const dateStr of selectedDates) {
      const dow = parseLocalDate(dateStr).getDay();
      if ((dow === 5 || dow === 6) && weRate) { weekendNights++; weekendNet += weRate; }
      else { weekdayNights++; weekdayNet += wdRate; }
    }
    return { net: weekdayNet + weekendNet, weekdayNights, weekendNights, weekdayNet, weekendNet };
  };

  const priceCalc = calcPrice();
  const net = priceCalc.net;
  const vat = net * 0.135;
  const gross = net + vat;
  const stripeFee = gross * 0.015;
  const total = gross + stripeFee;

  const currencySymbol = selectedListing
    ? CURRENCY_SYMBOLS[selectedListing.currency] || selectedListing.currency.toUpperCase()
    : "€";

  const isFormValid =
    guest.guest_name.trim().length > 0 &&
    guest.guest_email.trim().includes("@") &&
    guest.guest_phone.trim().length > 0;

  const canPay = selectedListing && nights > 0 && isFormValid;

  const handleDayClick = (day, modifiers) => {
    if (modifiers.disabled) return;
    const dateStr = format(day, "yyyy-MM-dd");
    if (selectedDates.has(dateStr)) {
      const next = new Set(selectedDates);
      next.delete(dateStr);
      setSelectedDates(next);
      setAnchorDate(null);
    } else if (!anchorDate) {
      const next = new Set(selectedDates);
      next.add(dateStr);
      setSelectedDates(next);
      setAnchorDate(dateStr);
    } else {
      const blocked = new Set(selectedListing?.blocked_dates || []);
      const today = new Date(); today.setHours(0, 0, 0, 0);
      const anchorD = parseLocalDate(anchorDate);
      const endD = new Date(day); endD.setHours(0, 0, 0, 0);
      const [startFill, endFill] = anchorD <= endD ? [anchorD, endD] : [endD, anchorD];
      const next = new Set(selectedDates);
      let added = 0, skippedBlocked = 0;
      const cur = new Date(startFill);
      while (cur <= endFill) {
        const ds = format(cur, "yyyy-MM-dd");
        const curNorm = new Date(cur); curNorm.setHours(0, 0, 0, 0);
        if (blocked.has(ds)) { skippedBlocked++; }
        else if (curNorm >= today) { if (!next.has(ds)) added++; next.add(ds); }
        cur.setDate(cur.getDate() + 1);
      }
      setSelectedDates(next);
      setAnchorDate(null);
      const total = next.size;
      if (skippedBlocked > 0) toast.success(`${total} night${total !== 1 ? "s" : ""} selected — ${skippedBlocked} unavailable skipped`);
      else if (added > 0) toast.success(`${total} night${total !== 1 ? "s" : ""} selected`);
    }
  };

  const handlePayNow = async () => {
    if (!canPay) return;
    if (!isFormValid) {
      toast.error("Please fill in your name, email, and phone number before paying.");
      return;
    }
    setLoading(true);
    try {
      const checkIn = sortedSelectedDates[0];
      const checkOut = format(addDays(parseLocalDate(sortedSelectedDates[sortedSelectedDates.length - 1]), 1), "yyyy-MM-dd");
      const res = await axios.post(`${API}/create-checkout-session`, {
        listing_id: selectedListing.id,
        check_in: checkIn,
        check_out: checkOut,
        nights,
        selected_dates: sortedSelectedDates,
        origin: window.location.origin,
        ...guest,
      });
      window.location.href = res.data.url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Payment error. Please try again.";
      toast.error(msg);
      setLoading(false);
    }
  };

  const calendarSelected = sortedSelectedDates.map(parseLocalDate);
  const anchorModifier = anchorDate ? [parseLocalDate(anchorDate)] : [];

  const guestField = (label, field, type = "text", placeholder = "", required = false) => (
    <div>
      <label className="block text-[10px] tracking-widest uppercase text-[#1C1917] font-bold mb-2 font-manrope">
        {label}{required && <span className="text-[#D4AF37] ml-0.5">*</span>}
      </label>
      <input
        data-testid={`guest-${field}`}
        type={type}
        value={guest[field]}
        onChange={(e) => setGuest((p) => ({ ...p, [field]: e.target.value }))}
        placeholder={placeholder}
        className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-sm text-[#1C1917] placeholder-[#C8C4C0] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope"
      />
    </div>
  );

  return (
    <div className="min-h-screen flex bg-[#FAFAF9]">
      {/* Admin Button */}
      <button
        data-testid="admin-login-btn"
        onClick={() => navigate("/admin/login")}
        title="Admin Login"
        className="fixed top-5 right-5 z-50 p-2.5 text-[#1C1917]/30 hover:text-[#D4AF37] transition-colors duration-300 hover:bg-[#1C1917]/5 rounded-sm"
      >
        <Lock size={18} />
      </button>

      {/* Left Panel */}
      <div className="hidden lg:block fixed left-0 top-0 w-1/2 h-screen overflow-hidden">
        <img src={HERO_IMAGE} alt="Goldline Management" className="object-cover w-full h-full" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/10 to-transparent" />
        <div className="absolute top-8 left-8 flex items-center gap-3 z-10">
          <img src={GLM_LOGO} alt="GLM" className="w-14 h-14 rounded-sm object-cover flex-shrink-0 shadow-lg" />
          <div className="flex flex-col justify-center" style={{ height: "56px" }}>
            <p className="font-playfair text-sm font-semibold leading-tight tracking-tight text-[#D4AF37]">Goldline</p>
            <p className="font-playfair text-sm font-semibold leading-tight tracking-tight text-[#D4AF37]">Management</p>
            <p className="font-playfair text-sm font-semibold leading-tight tracking-tight text-[#D4AF37]">Limited</p>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-12 text-white">
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/50 mb-3 font-manrope">Premium Catalog · Dublin</p>
          <p className="text-white/55 mt-2 text-sm font-manrope leading-relaxed">
            Choose your property, pick your dates,<br />and pay securely.
          </p>
        </div>
      </div>

      {/* Right Panel */}
      <div className="w-full lg:w-1/2 lg:ml-[50%] min-h-screen">
        {/* Mobile Hero */}
        <div className="lg:hidden relative h-56 overflow-hidden">
          <img src={HERO_IMAGE} alt="Goldline" className="object-cover w-full h-full" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-black/10 flex flex-col justify-end p-6">
            <p className="text-[10px] tracking-[0.25em] uppercase text-white/50 mb-2">Premium Catalog</p>
            <div className="flex items-center gap-3">
              <img src={GLM_LOGO} alt="GLM" className="w-11 h-11 rounded-sm object-cover flex-shrink-0 shadow" />
              <div>
                <p className="font-playfair text-lg font-semibold text-white leading-tight">Goldline</p>
                <p className="font-playfair text-lg font-semibold text-white leading-tight">Management</p>
                <p className="font-playfair text-lg font-semibold text-white leading-tight">Limited</p>
              </div>
            </div>
          </div>
        </div>

        <div className="px-6 lg:px-14 py-10 lg:py-16 max-w-lg lg:max-w-none">
          <div className="hidden lg:block mb-10">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#A8A29E] mb-2 font-manrope">Payment Portal</p>
            <h2 className="text-4xl font-playfair text-[#1C1917] tracking-tight">Book Your Stay</h2>
          </div>

          {/* 01 — Choose Property */}
          <section className="mb-10">
            <h3
              data-testid="section-choose-property"
              className="text-[10px] tracking-[0.25em] uppercase text-[#A8A29E] mb-5 font-manrope"
            >
              01 — Choose Property
            </h3>
            {fetching ? (
              <div className="flex items-center gap-2 text-[#A8A29E] text-sm py-4">
                <Loader2 className="animate-spin" size={16} />
                <span>Loading properties…</span>
              </div>
            ) : (
              <div className="space-y-3">
                {listings.map((listing) => (
                  <button
                    key={listing.id}
                    data-testid={`listing-card-${listing.id}`}
                    onClick={() => {
                      setSelectedListing(listing);
                      setSelectedDates(new Set());
                      setAnchorDate(null);
                      toast.success(`${listing.name} selected`);
                    }}
                    className={`w-full text-left p-4 border transition-colors duration-300 rounded-sm group ${
                      selectedListing?.id === listing.id
                        ? "border-[#D4AF37] bg-[#D4AF37]/5"
                        : "border-[#E7E5E4] bg-white hover:border-[#D4AF37]/40"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4">
                        <div className="w-16 h-16 rounded-sm overflow-hidden flex-shrink-0">
                          <img
                            src={listing.image_url || PROPERTY_IMAGES[listing.id] || PROPERTY_IMAGES.listing_1}
                            alt={listing.name}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="font-manrope font-semibold text-[#1C1917] text-sm">{listing.name}</p>
                          <p className="text-xs text-[#A8A29E] mt-0.5 leading-relaxed line-clamp-2 pr-2">{listing.description}</p>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2">
                        <p className="font-playfair text-lg font-semibold text-[#1C1917]">
                          {CURRENCY_SYMBOLS[listing.currency] || "€"}{listing.rate_per_night}
                        </p>
                        <p className="text-xs text-[#A8A29E]">/ night</p>
                      </div>
                    </div>
                    {selectedListing?.id === listing.id && (
                      <div className="mt-3 flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]" />
                        <span className="text-[10px] text-[#D4AF37] font-semibold tracking-widest uppercase font-manrope">Selected</span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </section>

          {/* 02 — Select Dates */}
          <section className="mb-10">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-[10px] tracking-[0.25em] uppercase text-[#A8A29E] font-manrope">02 — Select Dates</h3>
              {nights > 0 && (
                <button
                  data-testid="clear-dates-btn"
                  onClick={() => { setSelectedDates(new Set()); setAnchorDate(null); }}
                  className="flex items-center gap-1 text-[10px] text-[#A8A29E] hover:text-[#D4AF37] transition-colors font-manrope tracking-wide"
                >
                  <X size={12} />Clear ({nights} night{nights !== 1 ? "s" : ""})
                </button>
              )}
            </div>
            <div className="border border-[#E7E5E4] rounded-sm bg-white py-4 px-2 flex justify-center items-start overflow-hidden">
              <Calendar
                mode="multiple"
                selected={calendarSelected}
                onSelect={() => {}}
                onDayClick={handleDayClick}
                disabled={[
                  { before: new Date() },
                  ...(selectedListing?.blocked_dates || []).map((d) => {
                    const [y, m, day] = d.split("-").map(Number);
                    return new Date(y, m - 1, day);
                  }),
                ]}
                modifiers={{ anchor: anchorModifier }}
                modifiersClassNames={{ anchor: "ring-2 ring-[#D4AF37] ring-offset-1 rounded-sm" }}
                numberOfMonths={1}
                fixedWeeks
                showOutsideDays={false}
                classNames={{
                  months: "flex flex-col items-center w-full",
                  month: "w-full",
                  caption: "flex justify-center pt-2 pb-4 relative items-center px-10",
                  caption_label: "font-playfair text-base font-semibold text-[#1C1917] tracking-wide",
                  nav: "space-x-1 flex items-center",
                  nav_button: "h-8 w-8 bg-transparent p-0 text-[#A8A29E] hover:text-[#D4AF37] transition-colors inline-flex items-center justify-center rounded-sm",
                  nav_button_previous: "absolute left-2",
                  nav_button_next: "absolute right-2",
                  table: "w-full border-collapse",
                  head_row: "flex w-full",
                  head_cell: "flex-1 text-center text-[10px] tracking-widest uppercase text-[#A8A29E] font-manrope pb-3 font-normal",
                  row: "flex w-full mb-1",
                  cell: "flex-1 relative p-0 text-center focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#FEF3C7] [&:has([aria-selected].day-outside)]:bg-[#FEF3C7]/50 [&:has([aria-selected])]:rounded-sm",
                  day: "h-10 w-full p-0 font-normal aria-selected:opacity-100 font-manrope text-sm text-[#1C1917] hover:bg-[#F5F5F4] rounded-sm transition-colors inline-flex items-center justify-center",
                  day_selected: "bg-[#D4AF37] text-[#1C1917] hover:bg-[#C5A059] hover:text-[#1C1917] focus:bg-[#D4AF37] focus:text-[#1C1917] font-semibold rounded-sm",
                  day_today: "text-[#D4AF37] font-bold",
                  day_outside: "text-[#C8C4C0] opacity-40",
                  day_disabled: "text-[#C8C4C0] opacity-30 cursor-not-allowed",
                }}
              />
            </div>
            <p data-testid="calendar-hint" className="text-xs text-[#A8A29E] mt-2 font-manrope">
              {nights === 0
                ? "Click a date to start · click another to fill the range · click a selected date to remove it"
                : anchorDate
                ? "Range anchor set — now click another date to fill in between, or add individually"
                : `${nights} night${nights !== 1 ? "s" : ""} selected · click any date to add or remove`}
            </p>
          </section>

          {/* 03 — Guest Details */}
          <section className="mb-10">
            <h3 className="text-[10px] tracking-[0.25em] uppercase text-[#A8A29E] mb-5 font-manrope">
              03 — Guest Details
            </h3>
            <div className="bg-white border border-[#E7E5E4] rounded-sm p-6 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {guestField("Full Name", "guest_name", "text", "e.g. John Smith", true)}
                {guestField("Email Address", "guest_email", "email", "e.g. john@example.com", true)}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {guestField("Phone Number (with country code)", "guest_phone", "tel", "e.g. +353 87 123 4567", true)}
                <div>
                  <label className="block text-[10px] tracking-widests uppercase text-[#1C1917] font-bold mb-2 font-manrope">Number of Guests</label>
                  <input
                    data-testid="guest-guest_count"
                    type="number"
                    min="1"
                    max="20"
                    value={guest.guest_count}
                    onChange={(e) => setGuest((p) => ({ ...p, guest_count: parseInt(e.target.value) || 1 }))}
                    className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-sm text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[#1C1917] font-bold mb-2 font-manrope">Check-in Time</label>
                  <select
                    data-testid="guest-check_in_time"
                    value={guest.check_in_time}
                    onChange={(e) => setGuest((p) => ({ ...p, check_in_time: e.target.value }))}
                    className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-sm text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope cursor-pointer"
                  >
                    <option value="">Select check-in time</option>
                    {CHECK_IN_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-widest uppercase text-[#1C1917] font-bold mb-2 font-manrope">Check-out Time</label>
                  <select
                    data-testid="guest-check_out_time"
                    value={guest.check_out_time}
                    onChange={(e) => setGuest((p) => ({ ...p, check_out_time: e.target.value }))}
                    className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-sm text-[#1C1917] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 font-manrope cursor-pointer"
                  >
                    <option value="">Select check-out time</option>
                    {CHECK_OUT_TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-3 py-1">
                <input
                  data-testid="guest-parking_required"
                  id="parking"
                  type="checkbox"
                  checked={guest.parking_required}
                  onChange={(e) => setGuest((p) => ({ ...p, parking_required: e.target.checked }))}
                  className="w-4 h-4 accent-[#D4AF37] cursor-pointer flex-shrink-0"
                />
                <label htmlFor="parking" className="text-sm text-[#1C1917] font-manrope cursor-pointer">
                  Parking space required
                </label>
              </div>
              <div>
                <label className="block text-[10px] tracking-widests uppercase text-[#1C1917] font-bold mb-2 font-manrope">
                  Any Special Requirements
                </label>
                <textarea
                  data-testid="guest-special_requirements"
                  value={guest.special_requirements}
                  onChange={(e) => setGuest((p) => ({ ...p, special_requirements: e.target.value }))}
                  rows={3}
                  placeholder="e.g. early check-in, cot for infant, accessibility needs…"
                  className="w-full bg-transparent border-b border-[#E7E5E4] py-2.5 text-sm text-[#1C1917] placeholder-[#C8C4C0] focus:outline-none focus:border-[#D4AF37] transition-colors duration-200 resize-none leading-relaxed font-manrope"
                />
              </div>
              {!isFormValid && (guest.guest_name || guest.guest_email || guest.guest_phone) && (
                <p className="text-xs text-amber-600 font-manrope">
                  Please complete: {!guest.guest_name.trim() ? "Full name · " : ""}{!guest.guest_email.includes("@") ? "Valid email · " : ""}{!guest.guest_phone.trim() ? "Phone number" : ""}
                </p>
              )}
            </div>
          </section>

          {/* 04 — Summary & Payment */}
          <section>
            <h3 className="text-[10px] tracking-[0.25em] uppercase text-[#A8A29E] mb-5 font-manrope">
              04 — Summary & Payment
            </h3>

            {canPay ? (
              <div data-testid="booking-summary" className="bg-white border border-[#E7E5E4] rounded-sm p-6 mb-5">
                <div className="flex justify-between items-start gap-4 mb-4">
                  <div>
                    <p className="font-manrope font-semibold text-[#1C1917] text-sm">{selectedListing.name}</p>
                    <p className="text-xs text-[#A8A29E] mt-1">
                      {format(parseLocalDate(sortedSelectedDates[0]), "MMM d, yyyy")} &rarr;{" "}
                      {format(addDays(parseLocalDate(sortedSelectedDates[sortedSelectedDates.length - 1]), 1), "MMM d, yyyy")}
                    </p>
                    <p className="text-xs text-[#A8A29E] mt-0.5">{guest.guest_name} · {guest.guest_count} guest{guest.guest_count !== 1 ? "s" : ""}</p>
                  </div>
                  <span className="text-xs text-[#A8A29E] flex-shrink-0">{nights} night{nights !== 1 ? "s" : ""}</span>
                </div>
                <div className="space-y-2.5 text-sm border-t border-[#F5F5F4] pt-4">
                  {priceCalc.weekdayNights > 0 && priceCalc.weekendNights > 0 && selectedListing.weekend_rate ? (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-[#A8A29E] font-manrope">{priceCalc.weekdayNights} weekday night{priceCalc.weekdayNights !== 1 ? "s" : ""} &times; {currencySymbol}{selectedListing.rate_per_night}</span>
                        <span className="text-[#1C1917] font-manrope">{currencySymbol}{priceCalc.weekdayNet.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-[#A8A29E] font-manrope">{priceCalc.weekendNights} weekend night{priceCalc.weekendNights !== 1 ? "s" : ""} &times; {currencySymbol}{selectedListing.weekend_rate}</span>
                        <span className="text-[#1C1917] font-manrope">{currencySymbol}{priceCalc.weekendNet.toFixed(2)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="flex justify-between items-center">
                      <span className="text-[#A8A29E] font-manrope">{nights} night{nights !== 1 ? "s" : ""} &times; {currencySymbol}{selectedListing.rate_per_night}</span>
                      <span className="text-[#1C1917] font-manrope">{currencySymbol}{net.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-[#A8A29E] font-manrope">VAT (13.5%)</span>
                    <span className="text-[#1C1917] font-manrope">{currencySymbol}{vat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-[#A8A29E] font-manrope">Processing fee (1.5%)</span>
                    <span className="text-[#1C1917] font-manrope">{currencySymbol}{stripeFee.toFixed(2)}</span>
                  </div>
                  <div className="border-t border-dashed border-[#E7E5E4] pt-3 flex justify-between items-center">
                    <span className="text-[10px] tracking-widest uppercase text-[#A8A29E] font-manrope">Total</span>
                    <span data-testid="total-amount" className="font-playfair text-2xl font-semibold text-[#1C1917]">
                      {currencySymbol}{total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white border border-dashed border-[#E7E5E4] rounded-sm p-6 mb-5 text-center">
                <p className="text-sm text-[#A8A29E] font-manrope">
                  {!selectedListing
                    ? "Select a property to see your quote"
                    : nights === 0
                    ? "Pick your dates above"
                    : "Complete guest details above to continue"}
                </p>
              </div>
            )}

            <button
              data-testid="pay-now-btn"
              onClick={handlePayNow}
              disabled={!canPay || loading}
              className={`w-full h-14 font-manrope text-xs tracking-[0.2em] uppercase font-semibold rounded-sm transition-colors duration-300 active:scale-[0.99] ${
                canPay && !loading
                  ? "bg-[#1C1917] text-[#D4AF37] hover:bg-[#292524] cursor-pointer"
                  : "bg-[#E7E5E4] text-[#A8A29E] cursor-not-allowed"
              }`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 className="animate-spin" size={16} />
                  Redirecting to payment…
                </span>
              ) : (
                "Pay Now — Secure Checkout"
              )}
            </button>
            <p className="text-center text-[10px] text-[#A8A29E] mt-4 font-manrope tracking-wide">
              Secured by Stripe · 256-bit encryption
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default HomePage;
